/**
 * The Lore Stripe catalog: the canonical set of `lookup_key`s, product
 * metadata tags, display copy, subscription-item usage types, and env var
 * names that define our Stripe objects.
 *
 * Single source of truth. The bootstrap script (`scripts/bootstrap-stripe.ts`)
 * mints from this catalog; checkout, metering, and the credit runtime resolve
 * against the same constants. Keeping the strings here (not inlined in the
 * script) is what stops bootstrap / checkout / metering from drifting apart as
 * each grows its own copy of a lookup key or env var name.
 *
 * Meter event/payload keys live in `TOKEN_METER` (economics.ts) since they are
 * coupled to the metered-billing math; this module re-exports nothing from
 * there — import `TOKEN_METER` directly where you need it.
 */
import { CURRENT_PLAN_VERSION } from './economics.ts';

/**
 * Stripe Price `lookup_key`s — the stable handles we resolve prices by.
 * `transfer_lookup_key` moves a key onto a freshly minted price when the
 * spec changes, so these strings outlive any individual price id.
 */
export const PRICE_LOOKUP_KEYS = {
  free: 'lore_free',
  teamSeatMonthly: 'lore_team_seat_monthly',
  tokenMetered: 'lore_token_metered',
} as const;

/**
 * `metadata.lore_object` tags used to find our Products. Stripe Products
 * have no `lookup_key`, so we tag them and search by metadata instead.
 */
export const PRODUCT_TAGS = {
  free: 'plan_free',
  team: 'plan_team',
} as const;

/**
 * Subscription-item usage types. A Team subscription carries a `licensed`
 * seat item; once the metered token price is attached it ALSO carries a
 * `metered` token item. Seat-quantity edits and subscription mirroring must
 * therefore target the licensed item explicitly (never `items.data[0]`) —
 * see `selectLicensedSeatItem` in `apps/api/src/ee/billing/utils/shared.ts`.
 */
export const SEAT_USAGE_TYPE = 'licensed' as const;
export const METERED_USAGE_TYPE = 'metered' as const;

/**
 * Env var names that carry the resolved object ids. The bootstrap script
 * mints these in test mode and prints `KEY=value` lines for them; in live
 * mode the objects are created by hand in the Stripe dashboard and the env
 * vars are set from there (the bootstrap refuses non-test keys).
 */
export const STRIPE_PRICE_ENV_VARS = {
  creatorMonthlyPriceId: 'STRIPE_PRICE_CREATOR_MONTHLY',
  teamSeatPriceId: 'STRIPE_PRICE_TEAM_SEAT_MONTHLY',
  tokenMeteredPriceId: 'STRIPE_PRICE_TOKEN_METERED',
} as const;

export const STRIPE_ENV_VARS = {
  ...STRIPE_PRICE_ENV_VARS,
  tokenMeterId: 'STRIPE_TOKEN_METER_ID',
} as const;

/** Product display copy, also rendered into the Stripe dashboard. */
export const PRODUCT_COPY = {
  free: {
    name: 'Lore Free',
    description: 'Free tier — share threads, keep private, share skills.',
  },
  team: {
    name: 'Lore Team',
    description:
      'Team plan — $20/seat/mo, each seat grants a pooled $20/mo of AI credits.',
  },
} as const;

/**
 * Metadata stamped on a Team checkout **session**. `plan_version` is
 * stamped here so the credit runtime can resolve a team's grandfathered
 * economics from the subscription without re-deriving it.
 *
 * This and `teamSubscriptionMetadata` are the ONLY producers of
 * `plan_version` on Stripe. Mirror/persist target: when the credit ledger
 * lands (PR 1284+), it reads `plan_version` off the **subscription**
 * metadata (`mirrorSubscription`) — keep these helpers the single source so
 * the version cannot drift between session and subscription.
 */
export const teamCheckoutMetadata = (args: {
  organizationId: string;
  initiatingUserId: string;
}): Record<string, string> => ({
  lore_subject_type: 'organization',
  lore_subject_id: args.organizationId,
  lore_plan: 'team',
  plan_version: CURRENT_PLAN_VERSION,
  initiating_user_id: args.initiatingUserId,
});

/**
 * Metadata stamped on the Team **subscription** created by checkout.
 * Same `plan_version` as the session (above); omits the initiating user,
 * which is a session-only fact. This is the copy the credit runtime reads
 * at renewal to resolve the team's original economics.
 */
export const teamSubscriptionMetadata = (args: {
  organizationId: string;
}): Record<string, string> => ({
  lore_subject_type: 'organization',
  lore_subject_id: args.organizationId,
  lore_plan: 'team',
  plan_version: CURRENT_PLAN_VERSION,
});
